import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { PublishIcon } from '@/components/icons/PublishIcon'
import { useDocumentPageQuery } from '@/pages/DocumentPage/queries'
import { cn } from '@/utils'
import { CreateDocumentVersionForm } from './CreateDocumentVersionForm'

interface CreateDocumentVersionButtonProps {
  className?: string
  disabled?: boolean
  lastestVersionNumber: number
  documentVersionId?: string | null
}

export const CreateDocumentVersionButton = ({
  className,
  disabled,
  lastestVersionNumber,
  documentVersionId,
}: CreateDocumentVersionButtonProps) => {
  const [open, setOpen] = useState(false)
  const documentPage = useDocumentPageQuery()
  const documentId = documentPage.data?.documentId
  const projectId = documentPage.data?.project.id || ''

  if (!documentId || documentPage.isLoading) {
    return null
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => setOpen(isOpen)}>
      <DialogTrigger
        disabled={disabled}
        className={cn(className, {
          'cursor-default': disabled,
          'opacity-25': disabled,
        })}
        data-testid="create-document-version-button"
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                className="rounded-md hover:bg-gray-100 m-0 h-full w-auto flex p-1 items-center justify-center min-w-[50px]"
                style={{
                  backgroundColor: undefined,
                }}
              >
                <span className="flex items-center flex-col space-y-0.5 h-full justify-between">
                  <PublishIcon className="w-5 h-5 stroke-2" />
                  <span className="text-xs">Publish</span>
                </span>
              </Button>
            </TooltipTrigger>
            <TooltipContent className="bg-black text-white">
              <p>Publish document version</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </DialogTrigger>
      <DialogContent
        className="min-h-56 max-w-xl gap-0 pb-0"
        ariaLabel="Publish Document Version"
      >
        <DialogHeader>
          <DialogTitle>Publish Document Version</DialogTitle>
        </DialogHeader>
        <div className="my-3">
          {documentVersionId ? (
            <CreateDocumentVersionForm
              documentId={documentId}
              documentVersionId={documentVersionId}
              projectId={projectId}
              lastestVersionNumber={lastestVersionNumber}
            />
          ) : (
            <div>Missing current document version</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
